import React, { useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import * as Sentry from "@sentry/react"
import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, useMediaQuery, IconButton } from "@mui/material"
import { useTheme } from "@mui/material/styles"
// import SearchIcon from "@mui/icons-material/Search"
import SchoolIcon from "@mui/icons-material/School"
import MenuBookIcon from "@mui/icons-material/MenuBook"
// import ChatIcon from "@mui/icons-material/Chat"
// import HistoryIcon from "@mui/icons-material/History"
import LogoutIcon from "@mui/icons-material/Logout"
import HomeIcon from "@mui/icons-material/Home"
import ContactSupportIcon from "@mui/icons-material/ContactSupport"
import MenuIcon from "@mui/icons-material/Menu"
import PersonRemoveIcon from "@mui/icons-material/PersonRemove"
import UserContext from "../../context/UserContext"
import { F_INDEX, F_TRADING_SEARCH, F_VERWALTUNG_BOOKS, F_VERWALTUNG_LICENCES } from "../../constants/routes"
import ContactFormDialog from "../../components/ContactFormDialog"
import DeleteAccountDialog from "../../components/DeleteAccountDialog"
import { resetAllCaches } from "../../hooks/useFetch"

const DRAWER_WIDTH = 260

const RootContainer = ({ children, bookmanagement }) => {
  const [showSupportDialog, setShowSupportDialog] = useState(false)
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const navigate = useNavigate()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  const { userData, setUserDataExternal } = useContext(UserContext)
  const schoolName = bookmanagement ? userData.school.name : userData.user.name
  const headerCaption = bookmanagement ? userData.school.username : userData.user.email

  const handleLogout = () => {
    Sentry.setUser(null)
    Sentry.setTags({})
    setUserDataExternal(null)
    localStorage.clear()
    resetAllCaches()
    navigate(F_INDEX)
  }

  const NavigationList = bookmanagement ? (
    <>
      <ListItemButton onClick={() => navigate(F_VERWALTUNG_BOOKS)}>
        <ListItemIcon>
          <MenuBookIcon />
        </ListItemIcon>
        <ListItemText primary="Bücherlisten" />
      </ListItemButton>
      <ListItemButton onClick={() => navigate(F_VERWALTUNG_LICENCES)}>
        <ListItemIcon>
          <SchoolIcon />
        </ListItemIcon>
        <ListItemText primary="Lizenzen" />
      </ListItemButton>
    </>
  ) : (
    <ListItemButton onClick={() => navigate(F_TRADING_SEARCH)}>
      <ListItemIcon>
        <MenuBookIcon />
      </ListItemIcon>
      <ListItemText primary="Bücherlisten" />
    </ListItemButton>
  )

  const DrawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ p: 3, pt: 5, backgroundColor: "secondary.light" }}>
        <Typography variant="h6" sx={{ color: "#FFF", lineHeight: 1.3 }}>
          {schoolName}
        </Typography>
        <Typography variant="caption" sx={{ color: "#FFF", mt: 0.5 }}>
          {headerCaption}
        </Typography>
      </Box>

      <Divider />

      {/* Navigation */}
      <List>{NavigationList}</List>

      <Divider sx={{ mt: "auto" }} />

      {/* Footer */}
      <List>
        <ListItemButton onClick={() => navigate(F_INDEX)}>
          <ListItemIcon>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText primary="Startseite" />
        </ListItemButton>

        {!bookmanagement && (
          <>
            <ListItemButton onClick={() => setShowSupportDialog(true)}>
              <ListItemIcon>
                <ContactSupportIcon />
              </ListItemIcon>
              <ListItemText primary="Support" />
            </ListItemButton>

            <ListItemButton onClick={() => setShowDeleteAccountDialog(true)}>
              <ListItemIcon>
                <PersonRemoveIcon />
              </ListItemIcon>
              <ListItemText primary="Account löschen" />
            </ListItemButton>
          </>
        )}

        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>

      <Box sx={{ textAlign: "center", pb: 2 }}>
        <Typography variant="caption" color="secondary.main">
          made in berlin
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: "flex" }}>
      {!isDesktop && (
        <IconButton
          onClick={() => setMenuOpen(true)}
          sx={{
            position: "fixed",
            left: 2,
            top: 2,
            zIndex: 1200,
          }}>
          <MenuIcon sx={{ color: "secondary.main" }} />
        </IconButton>
      )}

      <Drawer
        variant={isDesktop && "permanent"}
        anchor="left"
        sx={
          isDesktop && {
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
            },
          }
        }
        // mobile

        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={
          !isDesktop && {
            sx: {
              width: "80vw",
              maxWidth: 320,
              borderTopRightRadius: 16,
              borderBottomRightRadius: 16,
            },
          }
        }>
        {DrawerContent}
      </Drawer>

      <Box component="main" sx={{ width: "100%" }}>
        {children}
      </Box>

      {showSupportDialog && (
        <ContactFormDialog
          text="Hast Du eine Frage? Hier kannst Du Kontakt zum Schuladministrator und dem Bookay-Support aufnehmen."
          placeholder="Beschreibe dein Anliegen..."
          handleClose={() => setShowSupportDialog(false)}
        />
      )}

      {showDeleteAccountDialog && <DeleteAccountDialog handleClose={() => setShowDeleteAccountDialog(false)} onConfirm={handleLogout} />}
    </Box>
  )
}

export default RootContainer
